// ==================================================================================
// Digital Approval System (DAS) - Consolidation Endpoints
// ==================================================================================
// API endpoint definitions for Two-Level Approval / Consolidation operations
// ==================================================================================

import { buildEndpoint, buildEndpointWithQuery } from '../base.endpoints';
import type { PreApprovedRequestQueryParameters } from '../../types/das/consolidation.types';

const BASE_PATH = '/das/consolidated-approvals';

/**
 * Consolidation endpoints for Two-Level Approval workflow
 */
export const consolidationEndpoints = {
  // ==================================================================================
  // PRE-APPROVAL ENDPOINTS
  // ==================================================================================

  /**
   * Pre-approve a request (first level of two-level approval)
   * POST /das/consolidated-approvals/requests/{requestId}/pre-approve
   */
  preApprove: (requestId: string) =>
    buildEndpoint(`${BASE_PATH}/requests/${requestId}/pre-approve`),

  /**
   * Check if current user can pre-approve a request
   * GET /das/consolidated-approvals/requests/{requestId}/can-pre-approve
   */
  canPreApprove: (requestId: string) =>
    buildEndpoint(`${BASE_PATH}/requests/${requestId}/can-pre-approve`),

  /**
   * Get pre-approved requests available for consolidation
   * GET /das/consolidated-approvals/pre-approved
   */
  getPreApproved: (params?: PreApprovedRequestQueryParameters) => {
    const endpoint = buildEndpoint(`${BASE_PATH}/pre-approved`);
    if (!params) return endpoint;
    return buildEndpointWithQuery(`${BASE_PATH}/pre-approved`, params as Record<string, unknown>);
  },

  /**
   * Get pre-approved requests by request type
   * GET /das/consolidated-approvals/pre-approved/by-type/{requestTypeId}
   */
  getPreApprovedByType: (requestTypeId: string, excludeAlreadyConsolidated = true) =>
    buildEndpoint(
      `${BASE_PATH}/pre-approved/by-type/${requestTypeId}?excludeAlreadyConsolidated=${excludeAlreadyConsolidated}`
    ),

  // ==================================================================================
  // CONSOLIDATION CREATION & MANAGEMENT ENDPOINTS
  // ==================================================================================

  /**
   * Create a consolidated approval request
   * POST /das/consolidated-approvals
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * Get a consolidated request with all its child items
   * GET /das/consolidated-approvals/{id}
   */
  getById: (id: string) => buildEndpoint(`${BASE_PATH}/${id}`),

  /**
   * Get all consolidated requests with optional status filter
   * GET /das/consolidated-approvals
   */
  getAll: (status?: string, pageNumber = 1, pageSize = 20) => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (status) params.status = status;
    return buildEndpointWithQuery(BASE_PATH, params);
  },

  /**
   * Get child requests for a consolidated request
   * GET /das/consolidated-approvals/{id}/children
   */
  getChildren: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/children`),

  /**
   * Add pre-approved requests to an existing consolidation
   * POST /das/consolidated-approvals/{id}/add-items
   */
  addItems: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/add-items`),

  /**
   * Remove requests from a consolidation
   * POST /das/consolidated-approvals/{id}/remove-items
   */
  removeItems: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/remove-items`),

  /**
   * Undo a consolidation - reverts all child requests back to Pre Approved status
   * POST /das/consolidated-approvals/{id}/undo
   */
  undo: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/undo`),

  /**
   * Check if current user can undo a consolidation
   * GET /das/consolidated-approvals/{id}/can-undo
   */
  canUndo: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/can-undo`),

  // ==================================================================================
  // FINAL APPROVAL ENDPOINTS
  // ==================================================================================

  /**
   * Give final approval to a consolidated request
   * POST /das/consolidated-approvals/{id}/final-approve
   */
  finalApprove: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/final-approve`),

  /**
   * Reject a consolidated request
   * POST /das/consolidated-approvals/{id}/final-reject
   */
  finalReject: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/final-reject`),

  /**
   * Check if current user can give final approval
   * GET /das/consolidated-approvals/{id}/can-final-approve
   */
  canFinalApprove: (id: string) => buildEndpoint(`${BASE_PATH}/${id}/can-final-approve`),

  // ==================================================================================
  // VALIDATION ENDPOINTS
  // ==================================================================================

  /**
   * Validate requests for consolidation
   * POST /das/consolidated-approvals/validate
   */
  validate: () => buildEndpoint(`${BASE_PATH}/validate`),

  /**
   * Check if a request is a consolidated request
   * GET /das/consolidated-approvals/requests/{requestId}/is-consolidated
   */
  isConsolidated: (requestId: string) =>
    buildEndpoint(`${BASE_PATH}/requests/${requestId}/is-consolidated`),

  /**
   * Check if a request is part of a consolidation
   * GET /das/consolidated-approvals/requests/{requestId}/is-in-consolidation
   */
  isInConsolidation: (requestId: string) =>
    buildEndpoint(`${BASE_PATH}/requests/${requestId}/is-in-consolidation`),

  /**
   * Get the parent consolidated request for a child request
   * GET /das/consolidated-approvals/requests/{requestId}/parent
   */
  getParent: (requestId: string) =>
    buildEndpoint(`${BASE_PATH}/requests/${requestId}/parent`),

  // ==================================================================================
  // STATISTICS ENDPOINTS
  // ==================================================================================

  /**
   * Get statistics for two-level approval workflow
   * GET /das/consolidated-approvals/statistics
   */
  getStatistics: (entityId?: string) => {
    const endpoint = buildEndpoint(`${BASE_PATH}/statistics`);
    return entityId ? `${endpoint}?entityId=${entityId}` : endpoint;
  },

  /**
   * Get count of pre-approved requests awaiting consolidation
   * GET /das/consolidated-approvals/statistics/pending-consolidation-count
   */
  getPendingConsolidationCount: (requestTypeId?: string) => {
    const endpoint = buildEndpoint(`${BASE_PATH}/statistics/pending-consolidation-count`);
    return requestTypeId ? `${endpoint}?requestTypeId=${requestTypeId}` : endpoint;
  },

  /**
   * Get count of consolidated requests pending final approval
   * GET /das/consolidated-approvals/statistics/pending-final-approval-count
   */
  getPendingFinalApprovalCount: () =>
    buildEndpoint(`${BASE_PATH}/statistics/pending-final-approval-count`),

  // ==================================================================================
  // CONFIGURATION ENDPOINTS
  // ==================================================================================

  /**
   * Get consolidation workflow configuration
   * GET /das/consolidated-approvals/config
   */
  getConfig: (enterpriseId?: string, entityId?: string) => {
    const params: Record<string, unknown> = {};
    if (enterpriseId) params.enterpriseId = enterpriseId;
    if (entityId) params.entityId = entityId;
    return Object.keys(params).length > 0
      ? buildEndpointWithQuery(`${BASE_PATH}/config`, params)
      : buildEndpoint(`${BASE_PATH}/config`);
  },

  /**
   * Check if consolidation is required before final approval
   * GET /das/consolidated-approvals/config/is-consolidation-required
   */
  isConsolidationRequired: (enterpriseId?: string, entityId?: string) => {
    const params: Record<string, unknown> = {};
    if (enterpriseId) params.enterpriseId = enterpriseId;
    if (entityId) params.entityId = entityId;
    return Object.keys(params).length > 0
      ? buildEndpointWithQuery(`${BASE_PATH}/config/is-consolidation-required`, params)
      : buildEndpoint(`${BASE_PATH}/config/is-consolidation-required`);
  },
} as const;
